import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { contactInfo } from "@/mocks/site";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db6geajghk82orf0cl9g";

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "联系我们 - 美味的快递 | 客服热线与在线留言",
    "联系美味的快递：拨打 400-880-1234 客服热线，或在线留言咨询外卖与快递服务，我们将在 24 小时内回复你的问题。",
    "美味的快递,联系我们,客服电话,在线留言,外卖咨询,快递咨询"
  );

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const fd = new FormData(formEl);

    // honeypot check before any request
    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      const val = String(value).trim();
      if (val) payload.append(key, val);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        (parsed && (parsed.meta?.message || parsed.message || parsed.meta?.detail)) || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试，或直接致电 400-880-1234。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试，或直接致电 400-880-1234。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <div className="bg-background-50">
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是美食订单、快递寄送还是合作咨询，都可以通过以下方式找到我们，客服 7×24 小时在线。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {contactInfo.map((c) => (
            <div key={c.label} className="rounded-lg border border-background-200 bg-background-100 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${c.icon} text-2xl`} />
              </span>
              <h2 className="mt-4 text-sm font-medium text-foreground-500">{c.label}</h2>
              <p className="mt-1 font-heading text-sm font-bold text-foreground-950">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-extrabold text-foreground-950">在线留言</h2>
            <p className="mt-2 text-sm text-foreground-500">留下你的问题或建议，我们会在 24 小时内回复你。</p>

            {status === "success" && (
              <div className="mt-6 flex items-start gap-3 rounded-lg border border-accent-300 bg-accent-50 px-5 py-4 text-sm text-accent-800">
                <i className="ri-checkbox-circle-fill mt-0.5 text-lg text-accent-600" />
                <p>留言已提交成功，感谢你的反馈，我们会尽快与你联系！</p>
              </div>
            )}
            {status === "error" && (
              <div className="mt-6 flex items-start gap-3 rounded-lg border border-primary-300 bg-primary-50 px-5 py-4 text-sm text-primary-800">
                <i className="ri-error-warning-line mt-0.5 text-lg text-primary-600" />
                <p>{formError}</p>
              </div>
            )}

            <form
              id="contact-message-form"
              data-readdy-form
              onSubmit={onSubmit}
              noValidate
              className="mt-6 flex flex-col gap-5"
            >
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  姓名
                </label>
                <input id="contact-name" name="name" type="text" required placeholder="请输入你的姓名" className={inputClass} />
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  联系方式
                </label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="手机号或微信号，方便我们联系你"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的问题或建议，最多 500 字"
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* anti-spam field */}
              <input
                className="form-hp-alt"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-8 py-3.5 text-sm font-bold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} />
                {status === "submitting" ? "提交中..." : "提交留言"}
              </button>
            </form>
          </div>

          <div className="flex flex-col gap-6">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <iframe
                title="美味的快递总部位置 - 望京 SOHO"
                aria-label="美味的快递总部地图"
                src="https://samtom.cn/maps?q=Wangjing%20SOHO%20Beijing&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="h-[280px] w-full border-0 md:h-[340px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="flex items-start gap-3 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-map-pin-2-line text-xl" />
                </span>
                <div>
                  <p className="font-heading text-sm font-bold text-foreground-950">总部地址</p>
                  <p className="mt-1 text-sm text-foreground-600">北京市朝阳区望京 SOHO 塔 C 座 18 层</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-customer-service-2-line text-primary-600" />
                更快捷的服务
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                如果是订单或快递问题，建议直接拨打 400-880-1234，客服会更快为你处理。
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="tel:4008801234"
                  className="flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-50 transition hover:bg-accent-600"
                >
                  <i className="ri-phone-line" />
                  拨打客服热线
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}